/**
 * What is wrong with a studio or show-style config (D32), each problem at its path
 * (`pieceTypes.camera.sourceLayer: unknown source layer "x"`) — the engine's
 * schema checked in the browser while typing, or the server's
 * `validation-failed` issues after a save.
 */
import type { ConfigProblem } from '../../lib/studio/config.js';

export type ConfigProblemListProps = {
  readonly problems: readonly ConfigProblem[];
  /** Where they came from: checked here while typing, or refused by the server. */
  readonly source: 'editor' | 'server';
  /** Which of the engine's schemas judged it, for the "valid" line: `studio` (the default) or `show-style`. */
  readonly schema?: 'studio' | 'show-style';
};

export function ConfigProblemList({ problems, source, schema }: ConfigProblemListProps) {
  if (problems.length === 0) {
    return (
      <p className="text-sm" data-testid="config-valid">
        <span className="rounded bg-success px-1.5 py-0.5 text-success-foreground">Valid</span> <span className="text-muted-foreground">— the engine’s {schema ?? 'studio'} schema accepts it.</span>
      </p>
    );
  }
  return (
    <div role="alert" className="grid gap-1.5 rounded-md border border-destructive p-3" data-testid="config-problems">
      <p className="text-sm font-medium text-destructive">
        {problems.length} problem{problems.length === 1 ? '' : 's'}
        {source === 'server' ? ' (the server refused the save)' : ''}
      </p>
      <ul className="grid gap-1 text-sm">
        {problems.map((p, i) => (
          // A path can carry two problems; the list's order is stable (sorted by path).
          <li key={`${p.path}:${String(i)}`} data-testid="config-problem">
            <code className="rounded bg-muted px-1 font-mono text-xs">{p.path}</code> {p.message}
          </li>
        ))}
      </ul>
    </div>
  );
}
